import {
  GameStats,
  GameStatus,
  MLBGameStats,
  MLBGameTeamStats,
  MlbTeamStats,
  NBAGameTeamStats,
  NFLGameStats,
  NFLGameTeamStats,
  NHLGameStats,
  NHLGameTeamStats,
  NbaTeamStats,
  NflTeamStats,
  NhlTeamStats,
  Sport,
} from "common";
import { calculatePercentage, removeLeadingZeroDecimal } from "@/utils";

export interface StatComparison {
  group?: string;
  items: StatComparisonItem[];
}
export interface StatComparisonBar {
  title?: string;
  label?: string;
  value: number;
}
export interface StatComparisonItem {
  label: string;
  away: StatComparisonBar;
  home: StatComparisonBar;
}

export function createNflStatComparison(
  awayStats: NflTeamStats,
  homeStats: NflTeamStats
): StatComparison[] {
  let awayPointsPerGame: number = awayStats.pointsPerGame.value as number;
  return [
    {
      group: "Scoring",
      items: [
        {
          label: "Average Points / Game",
          away: {
            title: `${awayStats.pointsPerGame.value as number}`,
            value: calculatePercentage(
              awayStats.pointsPerGame.value as number,
              (awayStats.pointsPerGame.value as number) +
                (homeStats.pointsPerGame.value as number)
            ),
          },
          home: {
            title: `${homeStats.pointsPerGame.value as number}`,
            value: calculatePercentage(
              homeStats.pointsPerGame.value as number,
              (homeStats.pointsPerGame.value as number) +
                (awayStats.pointsPerGame.value as number)
            ),
          },
        },
        {
          label: "Average Points Against / Game",
          away: {
            title: `${awayStats.opponentPointsPerGame.value}`,
            value: calculatePercentage(
              awayStats.opponentPointsPerGame.value as number,
              (awayStats.opponentPointsPerGame.value as number) +
                (homeStats.opponentPointsPerGame.value as number)
            ),
          },
          home: {
            title: `${homeStats.opponentPointsPerGame.value}`,
            value: calculatePercentage(
              homeStats.opponentPointsPerGame.value as number,
              (awayStats.opponentPointsPerGame.value as number) +
                (homeStats.opponentPointsPerGame.value as number)
            ),
          },
        },
        {
          label: "Touchdowns",
          away: {
            title: `${awayStats.touchdowns}`,
            value: calculatePercentage(
              awayStats.touchdowns,
              awayStats.touchdowns + homeStats.touchdowns
            ),
          },
          home: {
            title: `${homeStats.touchdowns}`,
            value: calculatePercentage(
              homeStats.touchdowns,
              awayStats.touchdowns + homeStats.touchdowns
            ),
          },
        },
      ],
    },
    {
      group: "Passing",
      items: [
        {
          label: "Passing Yards",
          away: {
            title: `${awayStats.passingYards}`,
            value: calculatePercentage(
              awayStats.passingYards as number,
              (awayStats.passingYards as number) +
                (homeStats.passingYards as number)
            ),
          },
          home: {
            title: `${homeStats.passingYards}`,
            value: calculatePercentage(
              homeStats.passingYards as number,
              (awayStats.passingYards as number) +
                (homeStats.passingYards as number)
            ),
          },
        },
        {
          label: "Average Passing Yards / Game",
          away: {
            title: `${awayStats.passingYardsPerGame.value}`,
            value: calculatePercentage(
              awayStats.passingYardsPerGame.value as number,
              (awayStats.passingYardsPerGame.value as number) +
                (homeStats.passingYardsPerGame.value as number)
            ),
          },
          home: {
            title: `${homeStats.passingYardsPerGame.value}`,
            value: calculatePercentage(
              homeStats.passingYardsPerGame.value as number,
              (awayStats.passingYardsPerGame.value as number) +
                (homeStats.passingYardsPerGame.value as number)
            ),
          },
        },
        {
          label: "Passing Comps / Atts",
          away: {
            title: `${calculatePercentage(
              awayStats.passingAttempts,
              awayStats.passingCompletions
            )}`,
            value: calculatePercentage(
              awayStats.passingCompletions,
              awayStats.passingCompletions + homeStats.passingCompletions
            ),
          },
          home: {
            title: `${calculatePercentage(
              homeStats.passingAttempts,
              homeStats.passingCompletions
            )}`,
            value: calculatePercentage(
              homeStats.passingCompletions,
              awayStats.passingCompletions + homeStats.passingCompletions
            ),
          },
        },
      ],
    },
    {
      group: "Rushing",
      items: [
        {
          label: "Rushing Yards",
          away: {
            title: `${awayStats.rushingYards}`,
            value: calculatePercentage(
              awayStats.rushingYards as number,
              (awayStats.rushingYards as number) +
                (homeStats.rushingYards as number)
            ),
          },
          home: {
            title: `${homeStats.rushingYards}`,
            value: calculatePercentage(
              homeStats.rushingYards as number,
              (awayStats.rushingYards as number) +
                (homeStats.rushingYards as number)
            ),
          },
        },
        {
          label: "Average Rushing Yards / Game",
          away: {
            title: `${awayStats.rushingYardsPerGame.value}`,
            value: calculatePercentage(
              awayStats.rushingYardsPerGame.value as number,
              (awayStats.rushingYardsPerGame.value as number) +
                (homeStats.rushingYardsPerGame.value as number)
            ),
          },
          home: {
            title: `${homeStats.rushingYardsPerGame.value}`,
            value: calculatePercentage(
              homeStats.rushingYardsPerGame.value as number,
              (awayStats.rushingYardsPerGame.value as number) +
                (homeStats.rushingYardsPerGame.value as number)
            ),
          },
        },
        {
          label: "Rushing Attempts",
          away: {
            title: `${awayStats.rushingAttempts}`,
            value: calculatePercentage(
              awayStats.rushingAttempts,
              awayStats.rushingAttempts + homeStats.rushingAttempts
            ),
          },
          home: {
            title: `${homeStats.rushingAttempts}`,
            value: calculatePercentage(
              homeStats.rushingAttempts,
              awayStats.rushingAttempts + homeStats.rushingAttempts
            ),
          },
        },
      ],
    },
    {
      group: "Turnovers",
      items: [
        {
          label: "Fumbles",
          away: {
            title: `${awayStats.fumbles}`,
            value: calculatePercentage(
              awayStats.fumbles,
              awayStats.fumbles + homeStats.fumbles
            ),
          },
          home: {
            title: `${homeStats.fumbles}`,
            value: calculatePercentage(
              homeStats.fumbles,
              awayStats.fumbles + homeStats.fumbles
            ),
          },
        },
        {
          label: "Interceptions",
          away: {
            title: `${awayStats.interceptions}`,
            value: calculatePercentage(
              awayStats.interceptions,
              awayStats.interceptions + homeStats.interceptions
            ),
          },
          home: {
            title: `${homeStats.interceptions}`,
            value: calculatePercentage(
              homeStats.interceptions,
              awayStats.interceptions + homeStats.interceptions
            ),
          },
        },
      ],
    },
    {
      group: "Efficiency",
      items: [
        {
          label: "1st Downs",
          away: {
            title: `${awayStats.firstDowns}`,
            value: calculatePercentage(
              awayStats.firstDowns,
              awayStats.firstDowns + homeStats.firstDowns
            ),
          },
          home: {
            title: `${homeStats.firstDowns}`,
            value: calculatePercentage(
              homeStats.firstDowns,
              awayStats.firstDowns + homeStats.firstDowns
            ),
          },
        },
        {
          label: "3rd Down",
          away: {
            title: `${awayStats.thirdDown}`,
            value: calculatePercentage(
              awayStats.thirdDown,
              awayStats.thirdDown + homeStats.thirdDown
            ),
          },
          home: {
            title: `${homeStats.thirdDown}`,
            value: calculatePercentage(
              homeStats.thirdDown,
              awayStats.thirdDown + homeStats.thirdDown
            ),
          },
        },
        {
          label: "4th Down",
          away: {
            title: `${awayStats.fourthDown}`,
            value: calculatePercentage(
              awayStats.fourthDown,
              awayStats.fourthDown + homeStats.fourthDown
            ),
          },
          home: {
            title: `${homeStats.fourthDown}`,
            value: calculatePercentage(
              homeStats.fourthDown,
              awayStats.fourthDown + homeStats.fourthDown
            ),
          },
        },
        {
          label: "Redzone",
          away: {
            title: `${awayStats.redzone}`,
            value: calculatePercentage(
              awayStats.redzone,
              awayStats.redzone + homeStats.redzone
            ),
          },
          home: {
            title: `${homeStats.redzone}`,
            value: calculatePercentage(
              homeStats.redzone,
              awayStats.redzone + homeStats.redzone
            ),
          },
        },
      ],
    },
  ];
}

export function createNbaStatsComparison(
  awayStats: NbaTeamStats,
  homeStats: NbaTeamStats,
  status: GameStatus
): StatComparison[] {
  return [
    {
      group: "Offense",
      items: [
        ...(status === "Scheduled"
          ? [
              {
                label: "Average Points / Game",
                away: {
                  title: `${parseFloat(removeLeadingZeroDecimal(
                    awayStats.pointsPerGame.value as number
                  )).toFixed(1)}`,
                  value: calculatePercentage(
                    awayStats.pointsPerGame.value as number,
                    (awayStats.pointsPerGame.value as number) +
                      (homeStats.pointsPerGame.value as number)
                  ),
                },
                home: {
                  title: `${parseFloat(removeLeadingZeroDecimal(
                    homeStats.pointsPerGame.value as number
                  )).toFixed(1)}`,
                  value: calculatePercentage(
                    homeStats.pointsPerGame.value as number,
                    (homeStats.pointsPerGame.value as number) +
                      (awayStats.pointsPerGame.value as number)
                  ),
                },
              },
            ]
          : []),
        {
          label: "Field Goal %",
          away: {
            title: `${parseFloat(removeLeadingZeroDecimal(awayStats.fieldGoalPct)).toFixed(1)}`,
            value: calculatePercentage(
              awayStats.fieldGoalPct,
              awayStats.fieldGoalPct + homeStats.fieldGoalPct
            ),
          },
          home: {
            title: `${parseFloat(removeLeadingZeroDecimal(homeStats.fieldGoalPct)).toFixed(1)}`,
            value: calculatePercentage(
              homeStats.fieldGoalPct,
              homeStats.fieldGoalPct + awayStats.fieldGoalPct
            ),
          },
        },
        {
          label: "3 Point %",
          away: {
            title: `${parseFloat(removeLeadingZeroDecimal(awayStats.threePointersPct)).toFixed(1)}`,
            value: calculatePercentage(
              awayStats.threePointersPct,
              awayStats.threePointersPct + homeStats.threePointersPct
            ),
          },
          home: {
            title: `${parseFloat(removeLeadingZeroDecimal(homeStats.threePointersPct)).toFixed(1)}`,
            value: calculatePercentage(
              homeStats.threePointersPct,
              homeStats.threePointersPct + awayStats.threePointersPct
            ),
          },
        },
        {
          label: "Free Throw %",
          away: {
            title: `${parseFloat(removeLeadingZeroDecimal(awayStats.freeThrowPct)).toFixed(1)}`,
            value: calculatePercentage(
              awayStats.freeThrowPct,
              awayStats.freeThrowPct + homeStats.freeThrowPct
            ),
          },
          home: {
            title: `${parseFloat(removeLeadingZeroDecimal(homeStats.freeThrowPct)).toFixed(1)}`,
            value: calculatePercentage(
              homeStats.freeThrowPct,
              homeStats.freeThrowPct + awayStats.freeThrowPct
            ),
          },
        },
        {
          label: "Assists / Game",
          away: {
            title: `${parseFloat(removeLeadingZeroDecimal(
              awayStats.assistsPerGame.value as number
            )).toFixed(1)}`,
            value: calculatePercentage(
              awayStats.assistsPerGame.value as number,
              (awayStats.assistsPerGame.value as number) +
                (homeStats.assistsPerGame.value as number)
            ),
          },
          home: {
            title: `${parseFloat(removeLeadingZeroDecimal(
              homeStats.assistsPerGame.value as number
            )).toFixed(1)}`,
            value: calculatePercentage(
              homeStats.assistsPerGame.value as number,
              (homeStats.assistsPerGame.value as number) +
                (awayStats.assistsPerGame.value as number)
            ),
          },
        },
        {
          label: "Offensive Rebounds / Game",
          away: {
            title: `${parseFloat(removeLeadingZeroDecimal(
              awayStats.offensiveReboundsPerGame.value as number
            )).toFixed(1)}`,
            value: calculatePercentage(
              awayStats.offensiveReboundsPerGame.value as number,
              (awayStats.offensiveReboundsPerGame.value as number) +
                (homeStats.offensiveReboundsPerGame.value as number)
            ),
          },
          home: {
            title: `${parseFloat(removeLeadingZeroDecimal(
              homeStats.offensiveReboundsPerGame.value as number
            )).toFixed(1)}`,
            value: calculatePercentage(
              homeStats.offensiveReboundsPerGame.value as number,
              (homeStats.offensiveReboundsPerGame.value as number) +
                (awayStats.offensiveReboundsPerGame.value as number)
            ),
          },
        },
      ],
    },
    {
      group: "Defense",
      items: [
        {
          label: "Average Points Allowed / Game",
          away: {
            title: `${parseFloat(removeLeadingZeroDecimal(
              awayStats.opponentPointsPerGame.value as number
            )).toFixed(1)}`,
            value: calculatePercentage(
              awayStats.opponentPointsPerGame.value as number,
              (awayStats.opponentPointsPerGame.value as number) +
                (homeStats.opponentPointsPerGame.value as number)
            ),
          },
          home: {
            title: `${parseFloat(removeLeadingZeroDecimal(
              homeStats.opponentPointsPerGame.value as number
            )).toFixed(1)}`,
            value: calculatePercentage(
              homeStats.opponentPointsPerGame.value as number,
              (awayStats.opponentPointsPerGame.value as number) +
                (homeStats.opponentPointsPerGame.value as number)
            ),
          },
        },
        {
          label: "Steals / Game",
          away: {
            title: `${parseFloat(removeLeadingZeroDecimal(
              awayStats.stealsPerGame.value as number
            )).toFixed(1)}`,
            value: calculatePercentage(
              awayStats.stealsPerGame.value as number,
              (awayStats.stealsPerGame.value as number) +
                (homeStats.stealsPerGame.value as number)
            ),
          },
          home: {
            title: `${parseFloat(removeLeadingZeroDecimal(
              homeStats.stealsPerGame.value as number
            )).toFixed(1)}`,
            value: calculatePercentage(
              homeStats.stealsPerGame.value as number,
              (awayStats.stealsPerGame.value as number) +
                (homeStats.stealsPerGame.value as number)
            ),
          },
        },
        {
          label: "Blocks / Game",
          away: {
            title: `${parseFloat(removeLeadingZeroDecimal(
              awayStats.blocksPerGame.value as number
            )).toFixed(1)}`,
            value: calculatePercentage(
              awayStats.blocksPerGame.value as number,
              (awayStats.blocksPerGame.value as number) +
                (homeStats.blocksPerGame.value as number)
            ),
          },
          home: {
            title: `${parseFloat(removeLeadingZeroDecimal(
              homeStats.blocksPerGame.value as number
            )).toFixed(1)}`,
            value: calculatePercentage(
              homeStats.blocksPerGame.value as number,
              (awayStats.blocksPerGame.value as number) +
                (homeStats.blocksPerGame.value as number)
            ),
          },
        },
        {
          label: "Defensive Rebounds / Game",
          away: {
            title: `${parseFloat(removeLeadingZeroDecimal(
              awayStats.defensiveReboundsPerGame.value as number
            )).toFixed(1)}`,
            value: calculatePercentage(
              awayStats.defensiveReboundsPerGame.value as number,
              (awayStats.defensiveReboundsPerGame.value as number) +
                (homeStats.defensiveReboundsPerGame.value as number)
            ),
          },
          home: {
            title: `${parseFloat(removeLeadingZeroDecimal(
              homeStats.defensiveReboundsPerGame.value as number
            )).toFixed(1)}`,
            value: calculatePercentage(
              homeStats.defensiveReboundsPerGame.value as number,
              (awayStats.defensiveReboundsPerGame.value as number) +
                (homeStats.defensiveReboundsPerGame.value as number)
            ),
          },
        },
      ],
    },
  ];
}

export function createNhlStatsComparison(
  awayStats: NhlTeamStats,
  homeStats: NhlTeamStats,
  status: GameStatus
): StatComparison[] {
  return [
    {
      group: "Offense",
      items: [
        ...(status === "Scheduled"
          ? [
              {
                label: "Goals / Game",
                away: {
                  title: `${removeLeadingZeroDecimal(
                    awayStats.goalsPerGame.value as number
                  )}`,
                  value: calculatePercentage(
                    parseFloat(awayStats.goalsPerGame.value),
                    (parseFloat(awayStats.goalsPerGame.value)) +
                      (parseFloat(homeStats.goalsPerGame.value))
                  ),
                },
                home: {
                  title: `${removeLeadingZeroDecimal(
                    homeStats.goalsPerGame.value as number
                  )}`,
                  value: calculatePercentage(
                    parseFloat(homeStats.goalsPerGame.value),
                    (parseFloat(homeStats.goalsPerGame.value)) +
                      (parseFloat(awayStats.goalsPerGame.value))
                  ),
                },
              },
              {
                label: "Shots / Game",
                away: {
                  title: `${removeLeadingZeroDecimal(
                    awayStats.shotsPerGame as number
                  )}`,
                  value: calculatePercentage(
                    parseFloat(awayStats.shotsPerGame),
                    parseFloat(awayStats.shotsPerGame) + parseFloat(homeStats.shotsPerGame)
                  ),
                },
                home: {
                  title: `${removeLeadingZeroDecimal(
                    homeStats.shotsPerGame as number
                  )}`,
                  value: calculatePercentage(
                    parseFloat(homeStats.shotsPerGame),
                    parseFloat(homeStats.shotsPerGame) + parseFloat(awayStats.shotsPerGame)
                  ),
                },
              },
              // {
              //   label: "Power Play Goals",
              //   away: {
              //     label: `${awayStats.powerPlayGoals}`,
              //     value: calculatePercentage(
              //       awayStats.powerPlayGoals,
              //       awayStats.powerPlayGoals + homeStats.powerPlayGoals
              //     ),
              //   },
              //   home: {
              //     label: `${homeStats.powerPlayGoals}`,
              //     value: calculatePercentage(
              //       homeStats.powerPlayGoals,
              //       homeStats.powerPlayGoals + awayStats.powerPlayGoals
              //     ),
              //   },
              // },
              // {
              //   label: "Power Plays",
              //   away: {
              //     label: `${awayStats.powerPlays}`,
              //     value: calculatePercentage(
              //       awayStats.powerPlays,
              //       awayStats.powerPlays + homeStats.powerPlays
              //     ),
              //   },
              //   home: {
              //     label: `${homeStats.powerPlays}`,
              //     value: calculatePercentage(
              //       homeStats.powerPlays,
              //       homeStats.powerPlays + awayStats.powerPlays
              //     ),
              //   },
              // },
            ]
          : [
              {
                label: "Goals",
                away: {
                  title: `${awayStats.goals.value as number}`,
                  value: calculatePercentage(
                    parseFloat(awayStats.goals.value),
                    (parseFloat(awayStats.goals.value)) +
                      (parseFloat(homeStats.goals.value))
                  ),
                },
                home: {
                  title: `${homeStats.goals.value as number}`,
                  value: calculatePercentage(
                    parseFloat(homeStats.goals.value as number),
                    (parseFloat(homeStats.goals.value as number)) +
                      (parseFloat(awayStats.goals.value as number))
                  ),
                },
              },
              {
                label: "Shots",
                away: {
                  title: `${awayStats.shots.value as number}`,
                  value: calculatePercentage(
                    awayStats.shots.value as number,
                    (awayStats.shots.value as number) +
                      (homeStats.shots.value as number)
                  ),
                },
                home: {
                  title: `${homeStats.shots.value as number}`,
                  value: calculatePercentage(
                    homeStats.shots.value as number,
                    (homeStats.shots.value as number) +
                      (awayStats.shots.value as number)
                  ),
                },
              },
            ]),
        {
          label: "Power Play Goals",
          away: {
            title: `${awayStats.powerPlayGoals}`,
            value: calculatePercentage(
              awayStats.powerPlayGoals,
              awayStats.powerPlayGoals + homeStats.powerPlayGoals
            ),
          },
          home: {
            title: `${homeStats.powerPlayGoals}`,
            value: calculatePercentage(
              homeStats.powerPlayGoals,
              homeStats.powerPlayGoals + awayStats.powerPlayGoals
            ),
          },
        },
      ],
    },
    {
      group: "Defense",
      items: [
        ...(status === "Scheduled"
          ? [
              {
                label: "Goals Allowed / Game",
                away: {
                  title: `${removeLeadingZeroDecimal(
                    awayStats.opponentGoalsPerGame.value as number
                  )}`,
                  value: calculatePercentage(
                    parseFloat(awayStats.opponentGoalsPerGame.value),
                    (parseFloat(awayStats.opponentGoalsPerGame.value)) +
                      (parseFloat(homeStats.opponentGoalsPerGame.value))
                  ),
                },
                home: {
                  title: `${removeLeadingZeroDecimal(
                    homeStats.opponentGoalsPerGame.value as number
                  )}`,
                  value: calculatePercentage(
                    parseFloat(homeStats.opponentGoalsPerGame.value),
                    (parseFloat(awayStats.opponentGoalsPerGame.value)) +
                      (parseFloat(homeStats.opponentGoalsPerGame.value))
                  ),
                },
              },
              {
                label: "Shots Allowed / Game",
                away: {
                  title: `${removeLeadingZeroDecimal(
                    awayStats.opponentShotsPerGame.value as number
                  )}`,
                  value: calculatePercentage(
                    parseFloat(awayStats.opponentShotsPerGame.value),
                    (parseFloat(awayStats.opponentShotsPerGame.value)) +
                      (parseFloat(homeStats.opponentShotsPerGame.value))
                  ),
                },
                home: {
                  title: `${removeLeadingZeroDecimal(
                    homeStats.opponentShotsPerGame.value as number
                  )}`,
                  value: calculatePercentage(
                    parseFloat(homeStats.opponentShotsPerGame.value),
                    (parseFloat(homeStats.opponentShotsPerGame.value)) +
                      (parseFloat(awayStats.opponentShotsPerGame.value))
                  ),
                },
              },
              {
                label: "Power Play Goals Allowed",
                away: {
                  title: `${awayStats.opponentPowerPlayGoals}`,
                  value: calculatePercentage(
                    parseFloat(awayStats.opponentPowerPlayGoals),
                    parseFloat(awayStats.opponentPowerPlayGoals) +
                      parseFloat(homeStats.opponentPowerPlayGoals)
                  ),
                },
                home: {
                  title: `${homeStats.opponentPowerPlayGoals}`,
                  value: calculatePercentage(
                    parseFloat(homeStats.opponentPowerPlayGoals),
                    parseFloat(homeStats.opponentPowerPlayGoals) +
                      parseFloat(awayStats.opponentPowerPlayGoals)
                  ),
                },
              },
            ]
          : [
              {
                label: "Goals Allowed",
                away: {
                  title: `${awayStats.opponentGoals.value as number}`,
                  value: calculatePercentage(
                    awayStats.opponentGoals.value as number,
                    (awayStats.opponentGoals.value as number) +
                      (homeStats.opponentGoals.value as number)
                  ),
                },
                home: {
                  title: `${homeStats.opponentGoals.value as number}`,
                  value: calculatePercentage(
                    homeStats.opponentGoals.value as number,
                    (homeStats.opponentGoals.value as number) +
                      (awayStats.opponentGoals.value as number)
                  ),
                },
              },
              {
                label: "Shots Allowed",
                away: {
                  title: `${awayStats.opponentShots.value as number}`,
                  value: calculatePercentage(
                    awayStats.opponentShots.value as number,
                    (awayStats.opponentShots.value as number) +
                      (homeStats.opponentShots.value as number)
                  ),
                },
                home: {
                  title: `${homeStats.opponentShots.value as number}`,
                  value: calculatePercentage(
                    homeStats.opponentShots.value as number,
                    (homeStats.opponentShots.value as number) +
                      (awayStats.opponentShots.value as number)
                  ),
                },
              },
              {
                label: "Power Play Goals Allowed",
                away: {
                  title: `${awayStats.opponentPowerPlayGoals}`,
                  value: calculatePercentage(
                    awayStats.opponentPowerPlayGoals,
                    awayStats.opponentPowerPlayGoals +
                      homeStats.opponentPowerPlayGoals
                  ),
                },
                home: {
                  title: `${homeStats.opponentPowerPlayGoals}`,
                  value: calculatePercentage(
                    homeStats.opponentPowerPlayGoals,
                    homeStats.opponentPowerPlayGoals +
                      awayStats.opponentPowerPlayGoals
                  ),
                },
              },
            ]),
      ],
    },
  ];
}

// pregame
export function createMlbStatComparison(
  awayStats: MlbTeamStats,
  homeStats: MlbTeamStats
): StatComparison[] {
  return [
    {
      items: [
        {
          label: "Earned Run Average",
          away: {
            title: `${(awayStats.earnedRunAverage.value as number)}`,
            value: calculatePercentage(
              awayStats.earnedRunAverage.value as number,
              (awayStats.earnedRunAverage.value as number) +
                (homeStats.earnedRunAverage.value as number)
            ),
          },
          home: {
            title: `${(homeStats.earnedRunAverage.value as number)}`,
            value: calculatePercentage(
              homeStats.earnedRunAverage.value as number,
              (homeStats.earnedRunAverage.value as number) +
                (awayStats.earnedRunAverage.value as number)
            ),
          },
        },
        {
          label: "Hits",
          away: {
            title: `${awayStats.pitchingHits as number}`,
            value: calculatePercentage(
              awayStats.pitchingHits as number,
              (awayStats.pitchingHits as number) +
                (homeStats.pitchingHits as number)
            ),
          },
          home: {
            title: `${homeStats.pitchingHits as number}`,
            value: calculatePercentage(
              homeStats.pitchingHits as number,
              (awayStats.pitchingHits as number) +
                (homeStats.pitchingHits as number)
            ),
          },
        },
        
        {
          label: "Earned Runs",
          away: {
            title: `${awayStats.earnedRuns}`,
            value: calculatePercentage(
              awayStats.earnedRuns,
              awayStats.earnedRuns + homeStats.earnedRuns
            ),
          },
          home: {
            title: `${homeStats.earnedRuns}`,
            value: calculatePercentage(
              homeStats.earnedRuns,
              awayStats.earnedRuns + homeStats.earnedRuns
            ),
          },
        },
        {
          label: "Home Runs",
          away: {
            title: `${awayStats.pitchingHomeRuns.value as number}`,
            value: calculatePercentage(
              awayStats.pitchingHomeRuns.value as number,
              (awayStats.pitchingHomeRuns.value as number) +
                (homeStats.pitchingHomeRuns.value as number)
            ),
          },
          home: {
            title: `${homeStats.pitchingHomeRuns.value as number}`,
            value: calculatePercentage(
              homeStats.pitchingHomeRuns.value as number,
              (awayStats.pitchingHomeRuns.value as number) +
                (homeStats.pitchingHomeRuns.value as number)
            ),
          },
        },
       
        {
          label: "Bases on Balls / Walks",
          away: {
            title: awayStats.pitchingBasesOnBallsWalks.value as string,
            value: calculatePercentage(
              awayStats.pitchingBasesOnBallsWalks.value as number,
              (awayStats.pitchingBasesOnBallsWalks.value as number) +
                (homeStats.pitchingBasesOnBallsWalks.value as number)
            ),
          },
          home: {
            title: homeStats.pitchingBasesOnBallsWalks.value as string,
            value: calculatePercentage(
              homeStats.pitchingBasesOnBallsWalks.value as number,
              (awayStats.pitchingBasesOnBallsWalks.value as number) +
                (homeStats.pitchingBasesOnBallsWalks.value as number)
            ),
          },
        },
        {
          label: "Strikeouts",
          away: {
            title: `${awayStats.pitchingStrikeouts.value as number}`,
            value: calculatePercentage(
              awayStats.pitchingStrikeouts.value as number,
              (awayStats.pitchingStrikeouts.value as number) +
                (homeStats.pitchingStrikeouts.value as number)
            ),
          },
          home: {
            title: `${homeStats.pitchingStrikeouts.value as number}`,
            value: calculatePercentage(
              homeStats.pitchingStrikeouts.value as number,
              (awayStats.pitchingStrikeouts.value as number) +
                (homeStats.pitchingStrikeouts.value as number)
            ),
          },
        },
        {
          label: "Saves",
          away: {
            title: `${awayStats.saves.value as number}`,
            value: calculatePercentage(
              awayStats.saves.value as number,
              (awayStats.saves.value as number) +
                (homeStats.saves.value as number)
            ),
          },
          home: {
            title: `${homeStats.saves.value as number}`,
            value: calculatePercentage(
              homeStats.saves.value as number,
              (awayStats.saves.value as number) +
                (homeStats.saves.value as number)
            ),
          },
        },
        {
          label: "Walks + Hits / Inning Pitched",
          away: {
            title: `${awayStats.walksHitsPerInning}`,
            value: calculatePercentage(
              Number(awayStats.walksHitsPerInning),
              (Number(awayStats.walksHitsPerInning)) +
                (Number(homeStats.walksHitsPerInning))
            ),
          },
          home: {
            title: `${homeStats.walksHitsPerInning}`,
            value: calculatePercentage(
              Number(homeStats.walksHitsPerInning),
              (Number(awayStats.walksHitsPerInning)) +
                (Number(homeStats.walksHitsPerInning))
            ),
          },
        },
      ],
    },
    {
      items: [
        {
          label: "Batting Average",
          away: {
            title: awayStats.battingAverage.value as string,
            value: calculatePercentage(
              Number(awayStats.battingAverage.value),
              Number(awayStats.battingAverage.value) +
                Number(homeStats.battingAverage.value)
            ),
          },
          home: {
            title: homeStats.battingAverage.value as string,
            value: calculatePercentage(
              Number(homeStats.battingAverage.value),
              Number(awayStats.battingAverage.value) +
                Number(homeStats.battingAverage.value)
            ),
          },
        },
        {
          label: "At Bats",
          away: {
            title: `${awayStats.atBats}`,
            value: calculatePercentage(
              awayStats.atBats,
              awayStats.atBats + homeStats.atBats
            ),
          },
          home: {
            title: `${homeStats.atBats}`,
            value: calculatePercentage(
              homeStats.atBats,
              awayStats.atBats + homeStats.atBats
            ),
          },
        },
        {
          label: "Runs",
          away: {
            title: `${awayStats.runs.value as number}`,
            value: calculatePercentage(
              awayStats.runs.value as number,
              (awayStats.runs.value as number) +
                (homeStats.runs.value as number)
            ),
          },
          home: {
            title: `${homeStats.runs.value as number}`,
            value: calculatePercentage(
              homeStats.runs.value as number,
              (awayStats.runs.value as number) +
                (homeStats.runs.value as number)
            ),
          },
        },
        {
          label: "Hits",
          away: {
            title: `${awayStats.hits.value as number}`,
            value: calculatePercentage(
              awayStats.hits.value as number,
              (awayStats.hits.value as number) +
                (homeStats.hits.value as number)
            ),
          },
          home: {
            title: `${homeStats.hits.value as number}`,
            value: calculatePercentage(
              homeStats.hits.value as number,
              (awayStats.hits.value as number) +
                (homeStats.hits.value as number)
            ),
          },
        },
        {
          label: "Runs Batted In",
          away: {
            title: `${awayStats.runsBattedIn.value as number}`,
            value: calculatePercentage(
              awayStats.runsBattedIn.value as number,
              (awayStats.runsBattedIn.value as number) + (homeStats.runsBattedIn.value as number)
            ),
          },
          home: {
            title: `${homeStats.runsBattedIn.value as number}`,
            value: calculatePercentage(
              homeStats.runsBattedIn.value as number,
              (awayStats.runsBattedIn.value as number) + (homeStats.runsBattedIn.value as number)
            ),
          },
        },
        {
          label: "Home Runs",
          away: {
            title: `${awayStats.homeRuns.value as number}`,
            value: calculatePercentage(
              awayStats.homeRuns.value as number,
              (awayStats.homeRuns.value as number) +
                (homeStats.homeRuns.value as number)
            ),
          },
          home: {
            title: `${homeStats.homeRuns.value as number}`,
            value: calculatePercentage(
              homeStats.homeRuns.value as number,
              (awayStats.homeRuns.value as number) +
                (homeStats.homeRuns.value as number)
            ),
          },
        },
        {
          label: "Doubles",
          away: {
            title: `${awayStats.doubles}`,
            value: calculatePercentage(
              awayStats.doubles,
              awayStats.doubles + homeStats.doubles
            ),
          },
          home: {
            title: `${homeStats.doubles}`,
            value: calculatePercentage(
              homeStats.doubles,
              awayStats.doubles + homeStats.doubles
            ),
          },
        },
        {
          label: "Triples",
          away: {
            title: `${awayStats.triples}`,
            value: calculatePercentage(
              awayStats.triples,
              awayStats.triples + homeStats.triples
            ),
          },
          home: {
            title: `${homeStats.triples}`,
            value: calculatePercentage(
              homeStats.triples,
              awayStats.triples + homeStats.triples
            ),
          },
        },
        {
          label: "Total Bases",
          away: {
            title: `${awayStats.totalBases}`,
            value: calculatePercentage(
              awayStats.totalBases,
              awayStats.totalBases + homeStats.totalBases
            ),
          },
          home: {
            title: `${homeStats.totalBases}`,
            value: calculatePercentage(
              homeStats.totalBases,
              awayStats.totalBases + homeStats.totalBases
            ),
          },
        },
        {
          label: "Bases on Balls / Walks",
          away: {
            title: awayStats.basesOnBallsWalks.value as string,
            value: calculatePercentage(
              awayStats.basesOnBallsWalks.value as number,
              (awayStats.basesOnBallsWalks.value as number) +
                (homeStats.basesOnBallsWalks.value as number)
            ),
          },
          home: {
            title: homeStats.basesOnBallsWalks.value as string,
            value: calculatePercentage(
              homeStats.basesOnBallsWalks.value as number,
              (awayStats.basesOnBallsWalks.value as number) +
                (homeStats.basesOnBallsWalks.value as number)
            ),
          },
        },
        {
          label: "Strikeouts",
          away: {
            title: `${awayStats.strikeouts}`,
            value: calculatePercentage(
              awayStats.strikeouts,
              awayStats.strikeouts + homeStats.strikeouts
            ),
          },
          home: {
            title: `${homeStats.strikeouts}`,
            value: calculatePercentage(
              homeStats.strikeouts,
              awayStats.strikeouts + homeStats.strikeouts
            ),
          },
        },
        {
          label: "On Base %",
          away: {
            title: `${awayStats.onBasePct.value as number}`,
            value: calculatePercentage(
              Number(awayStats.onBasePct.value),
              Number(awayStats.onBasePct.value) + Number(homeStats.onBasePct.value)
            ),
          },
          home: {
            title: `${homeStats.onBasePct.value as number}`,
            value: calculatePercentage(
              Number(homeStats.onBasePct.value),
              Number(awayStats.onBasePct.value) + Number(homeStats.onBasePct.value)
            ),
          },
        },
     
      ],
    },
  ];
}

export function createGameStatComparison(data: GameStats, sport: Sport): StatComparison[] {
  switch (sport) {
    case "nfl":
      const nflData = data as NFLGameTeamStats;
      return [
        {
          group: "Offense",
          items: [
            {
              label: "Total Yards",
              away: {
                title: `${nflData.away.totalYards}`,
                value: calculatePercentage(
                  nflData.away.totalYards,
                  (nflData.away.totalYards) +
                    (nflData.home.totalYards)
                ),
              },
              home: {
                title: `${nflData.home.totalYards}`,
                value: calculatePercentage(
                  nflData.home.totalYards,
                  (nflData.home.totalYards) +
                    (nflData.away.totalYards)
                ),
              }
            },
            {
              label: "Total Plays",
              away: {
                title: `${nflData.away.totalPlays}`,
                value: calculatePercentage(
                  nflData.away.totalPlays,
                  (nflData.away.totalPlays) +
                    (nflData.home.totalPlays)
                ),
              },
              home: {
                title: `${nflData.home.totalPlays}`,
                value: calculatePercentage(
                  nflData.home.totalPlays,
                  (nflData.home.totalPlays) +
                    (nflData.away.totalPlays)
                ),
              }
            },
            {
              label: "1st Downs",
              away: {
                title: `${nflData.away.firstDowns}`,
                value: calculatePercentage(
                  nflData.away.firstDowns,
                  (nflData.away.firstDowns) +
                    (nflData.home.firstDowns)
                ),
              },
              home: {
                title: `${nflData.home.firstDowns}`,
                value: calculatePercentage(
                  nflData.home.firstDowns,
                  (nflData.home.firstDowns) +
                    (nflData.away.firstDowns)
                ),
              }
            },
          ]
        },
        {
          group: "Passing",
          items: [
            {
              label: "Passing Yards",
              away: {
                title: `${nflData.away.passingYards}`,
                value: calculatePercentage(
                  nflData.away.passingYards,
                  (nflData.away.passingYards) +
                    (nflData.home.passingYards)
                ),
              },
              home: {
                title: `${nflData.home.passingYards}`,
                value: calculatePercentage(
                  nflData.home.passingYards,
                  (nflData.home.passingYards) +
                    (nflData.away.passingYards)
                ),
              }
            },
            {
              label: "Passing Completions",
              away: {
                title: `${nflData.away.passingCompletions}`,
                value: calculatePercentage(
                  nflData.away.passingCompletions,
                  (nflData.away.passingCompletions) +
                    (nflData.home.passingCompletions)
                ),
              },
              home: {
                title: `${nflData.home.passingCompletions}`,
                value: calculatePercentage(
                  nflData.home.passingCompletions,
                  (nflData.home.passingCompletions) +
                    (nflData.away.passingCompletions)
                ),
              }
            },
            {
              label: "Passing Attempts",
              away: {
                title: `${nflData.away.passingAttempts}`,
                value: calculatePercentage(
                  nflData.away.passingAttempts,
                  (nflData.away.passingAttempts) +
                    (nflData.home.passingAttempts)
                ),
              },
              home: {
                title: `${nflData.home.passingAttempts}`,
                value: calculatePercentage(
                  nflData.home.passingAttempts,
                  (nflData.home.passingAttempts) +
                    (nflData.away.passingAttempts)
                ),
              }
            }
          ]
        },
        {
          group: "Rushing",
          items: [
            {
              label: "Rushing Yards",
              away: {
                title: `${nflData.away.rushingYards}`,
                value: calculatePercentage(
                  nflData.away.rushingYards,
                  (nflData.away.rushingYards) +
                    (nflData.home.rushingYards)
                ),
              },
              home: {
                title: `${nflData.home.rushingYards}`,
                value: calculatePercentage(
                  nflData.home.rushingYards,
                  (nflData.home.rushingYards) +
                    (nflData.away.rushingYards)
                ),
              }
            },
            {
              label: "Rushing Attempts",
              away: {
                title: `${nflData.away.rushingAttempts}`,
                value: calculatePercentage(
                  nflData.away.rushingAttempts,
                  (nflData.away.rushingAttempts) +
                    (nflData.home.rushingAttempts)
                ),
              },
              home: {
                title: `${nflData.home.rushingAttempts}`,
                value: calculatePercentage(
                  nflData.home.rushingAttempts,
                  (nflData.home.rushingAttempts) +
                    (nflData.away.rushingAttempts)
                ),
              }
            },
          ]
        },
        {
          group: "Efficiency",
          items: [
            {
              label: "3rd Down Efficiency",
              away: {
                title: `${nflData.away.thirdDownPct.toFixed(2)} (${nflData.away.thirdDownEfficiency})`,
                value: calculatePercentage(
                  nflData.away.thirdDownPct,
                  (nflData.away.thirdDownPct) +
                    (nflData.home.thirdDownPct)
                ),
              },
              home: {
                title: `${nflData.home.thirdDownPct.toFixed(2)} (${nflData.home.thirdDownEfficiency})`,
                value: calculatePercentage(
                  nflData.home.thirdDownPct,
                  (nflData.home.thirdDownPct) +
                    (nflData.away.thirdDownPct)
                ),
              }
            },
            {
              label: "4th Down Efficiency",
              away: {
                title: `${nflData.away.fourthDownPct} (${nflData.away.fourthDownEfficiency})`,
                value: calculatePercentage(
                  nflData.away.fourthDownPct,
                  (nflData.away.fourthDownPct) +
                    (nflData.home.fourthDownPct)
                ),
              },
              home: {
                title: `${nflData.home.fourthDownPct} (${nflData.home.fourthDownEfficiency})`,
                value: calculatePercentage(
                  nflData.home.fourthDownPct,
                  (nflData.home.fourthDownPct) +
                    (nflData.away.fourthDownPct)
                ),
              }
            },
          ]
        },
        {
          group: "Turnovers",
          items: [
            {
              label: "Interceptions",
              away: {
                title: `${nflData.away.interceptions}`,
                value: calculatePercentage(
                  nflData.away.interceptions,
                  (nflData.away.interceptions) +
                    (nflData.home.interceptions)
                ),
              },
              home: {
                title: `${nflData.home.interceptions}`,
                value: calculatePercentage(
                  nflData.home.interceptions,
                  (nflData.home.interceptions) +
                    (nflData.away.interceptions)
                ),
              }
            },
            {
              label: "Fumbles",
              away: {
                title: `${nflData.away.fumbles}`,
                value: calculatePercentage(
                  nflData.away.fumbles,
                  (nflData.away.fumbles) +
                    (nflData.home.fumbles)
                ),
              },
              home: {
                title: `${nflData.home.fumbles}`,
                value: calculatePercentage(
                  nflData.home.fumbles,
                  (nflData.home.fumbles) +
                    (nflData.away.fumbles)
                ),
              }
            },
          ]
        },
        {
          group: "Misc",
          items: [
            {
              label: "Sacks",
              away: {
                title: `${nflData.away.sacks}`,
                value: calculatePercentage(
                  nflData.away.sacks,
                  (nflData.away.sacks) +
                    (nflData.home.sacks)
                ),
              },
              home: {
                title: `${nflData.home.sacks}`,
                value: calculatePercentage(
                  nflData.home.sacks,
                  (nflData.home.sacks) +
                    (nflData.away.sacks)
                ),
              }
            },
            {
              label: "Penalties",
              away: {
                title: `${nflData.away.penalties}`,
                value: calculatePercentage(
                  nflData.away.penalties,
                  (nflData.away.penalties) +
                    (nflData.home.penalties)
                ),
              },
              home: {
                title: `${nflData.home.penalties}`,
                value: calculatePercentage(
                  nflData.home.penalties,
                  (nflData.home.penalties) +
                    (nflData.away.penalties)
                ),
              }
            },
            {
              label: "Penalty Yards",
              away: {
                title: `${nflData.away.penaltyYards}`,
                value: calculatePercentage(
                  nflData.away.penaltyYards,
                  (nflData.away.penaltyYards) +
                    (nflData.home.penaltyYards)
                ),
              },
              home: {
                title: `${nflData.home.penaltyYards}`,
                value: calculatePercentage(
                  nflData.home.penaltyYards,
                  (nflData.home.penaltyYards) +
                    (nflData.away.penaltyYards)
                ),
              }
            },
          ]
        }
      ]
    case "mlb":
      const mlbData = data as MLBGameTeamStats;
      return [
        {
          // group: "Pitching",
          items: [
            {
              label: "Earned Run Average",
              away: {
                title: `${(mlbData.away.era)}`,
                value: calculatePercentage(
                  mlbData.away.era,
                  (mlbData.away.era) +
                    (mlbData.home.era)
                ),
              },
              home: {
                title: `${(mlbData.home.era)}`,
                value: calculatePercentage(
                  mlbData.home.era,
                  (mlbData.home.era) +
                    (mlbData.away.era)
                ),
              },
            },
            {
              label: "Hits",
              away: {
                title: `${mlbData.away.pitchingHits}`,
                value: calculatePercentage(
                  mlbData.away.pitchingHits,
                  (mlbData.away.pitchingHits) +
                    (mlbData.home.pitchingHits)
                ),
              },
              home: {
                title: `${mlbData.home.pitchingHits}`,
                value: calculatePercentage(
                  mlbData.home.pitchingHits,
                  (mlbData.home.pitchingHits) +
                    (mlbData.away.pitchingHits)
                ),
              },
            },
            {
              label: "Runs",
              away: {
                title: `${mlbData.away.pitchingRuns}`,
                value: calculatePercentage(
                  mlbData.away.pitchingRuns,
                  (mlbData.away.pitchingRuns) +
                    (mlbData.home.pitchingRuns)
                ),
              },
              home: {
                title: `${mlbData.home.pitchingRuns}`,
                value: calculatePercentage(
                  mlbData.home.pitchingRuns,
                  (mlbData.home.pitchingRuns) +
                    (mlbData.away.pitchingRuns)
                ),
              },
            },
            {
              label: "Earned Runs",
              away: {
                title: `${mlbData.away.pitchingEarnedRuns}`,
                value: calculatePercentage(
                  mlbData.away.pitchingEarnedRuns,
                  mlbData.away.pitchingEarnedRuns + mlbData.home.pitchingEarnedRuns
                ),
              },
              home: {
                title: `${mlbData.home.pitchingEarnedRuns}`,
                value: calculatePercentage(
                  mlbData.home.pitchingEarnedRuns,
                  mlbData.home.pitchingEarnedRuns + mlbData.away.pitchingEarnedRuns
                ),
              },
            },
            {
              label: "Home Runs",
              away: {
                title: `${mlbData.away.pitchingHomeRuns}`,
                value: calculatePercentage(
                  mlbData.away.pitchingHomeRuns,
                  (mlbData.away.pitchingHomeRuns) +
                    (mlbData.home.pitchingHomeRuns)
                ),
              },
              home: {
                title: `${mlbData.home.pitchingHomeRuns}`,
                value: calculatePercentage(
                  mlbData.home.pitchingHomeRuns,
                  (mlbData.home.pitchingHomeRuns) +
                    (mlbData.away.pitchingHomeRuns)
                ),
              },
            },
            
            {
              label: "Bases on Balls / Walks",
              away: {
                title: `${mlbData.away.pitchingWalks}`,
                value: calculatePercentage(
                  mlbData.away.pitchingWalks,
                  (mlbData.away.pitchingWalks) +
                    (mlbData.home.pitchingWalks)
                ),
              },
              home: {
                title: `${mlbData.home.pitchingWalks}`,
                value: calculatePercentage(
                  mlbData.home.pitchingWalks,
                  (mlbData.home.pitchingWalks) +
                    (mlbData.away.pitchingWalks)
                ),
              },
            },
            {
              label: "Strikes",
              away: {
                title: `${mlbData.away.pitchingStrikes}`,
                value: calculatePercentage(
                  mlbData.away.pitchingStrikes,
                  (mlbData.away.pitchingStrikes) +
                    (mlbData.home.pitchingStrikes)
                ),
              },
              home: {
                title: `${mlbData.home.pitchingStrikes}`,
                value: calculatePercentage(
                  mlbData.home.pitchingStrikes,
                  (mlbData.home.pitchingStrikes) +
                    (mlbData.away.pitchingStrikes)
                ),
              },
            },
            {
              label: "Strikeouts",
              away: {
                title: `${mlbData.away.pitchingStrikeouts}`,
                value: calculatePercentage(
                  mlbData.away.pitchingStrikeouts,
                  (mlbData.away.pitchingStrikeouts) +
                    (mlbData.home.pitchingStrikeouts)
                ),
              },
              home: {
                title: `${mlbData.home.pitchingStrikeouts}`,
                value: calculatePercentage(
                  mlbData.home.pitchingStrikeouts,
                  (mlbData.home.pitchingStrikeouts) +
                    (mlbData.away.pitchingStrikeouts)
                ),
              },
            },
            {
              label: "Saves",
              away: {
                title: `${mlbData.away.saves}`,
                value: calculatePercentage(
                  mlbData.away.saves,
                  (mlbData.away.saves) +
                    (mlbData.home.saves)
                ),
              },
              home: {
                title: `${mlbData.home.saves}`,
                value: calculatePercentage(
                  mlbData.home.saves,
                  (mlbData.home.saves) +
                    (mlbData.away.saves)
                ),
              },
            },
          ],
        },
        {
          // group: "Hitting",
          items: [
            {
              label: "Batting Average",
              away: {
                title: mlbData.away.battingAverage,
                value: calculatePercentage(
                  Number(mlbData.away.battingAverage),
                  Number(mlbData.away.battingAverage) +
                    Number(mlbData.home.battingAverage)
                ),
              },
              home: {
                title: mlbData.home.battingAverage,
                value: calculatePercentage(
                  Number(mlbData.home.battingAverage),
                  Number(mlbData.home.battingAverage) +
                    Number(mlbData.away.battingAverage)
                ),
              },
            },
            {
              label: "At Bats",
              away: {
                title: `${mlbData.away.atBats}`,
                value: calculatePercentage(
                  mlbData.away.atBats,
                  mlbData.away.atBats + mlbData.home.atBats
                ),
              },
              home: {
                title: `${mlbData.home.atBats}`,
                value: calculatePercentage(
                  mlbData.home.atBats,
                  mlbData.home.atBats + mlbData.away.atBats
                ),
              },
            },
            {
              label: "Runs",
              away: {
                title: `${mlbData.away.runs}`,
                value: calculatePercentage(
                  mlbData.away.runs,
                  (mlbData.away.runs) +
                    (mlbData.home.runs)
                ),
              },
              home: {
                title: `${mlbData.home.runs}`,
                value: calculatePercentage(
                  mlbData.home.runs,
                  (mlbData.home.runs) +
                    (mlbData.away.runs)
                ),
              },
            },
            {
              label: "Hits",
              away: {
                title: `${mlbData.away.hits}`,
                value: calculatePercentage(
                  mlbData.away.hits,
                  (mlbData.away.hits) +
                    (mlbData.home.hits)
                ),
              },
              home: {
                title: `${mlbData.home.hits}`,
                value: calculatePercentage(
                  mlbData.home.hits,
                  (mlbData.home.hits) +
                    (mlbData.away.hits)
                ),
              },
            },
            {
              label: "Runs Batted In",
              away: {
                title: `${mlbData.away.runsBattedIn}`,
                value: calculatePercentage(
                  mlbData.away.runsBattedIn,
                  (mlbData.away.runsBattedIn) +
                    (mlbData.home.runsBattedIn)
                ),
              },
              home: {
                title: `${mlbData.home.runsBattedIn}`,
                value: calculatePercentage(
                  mlbData.home.runsBattedIn,
                  (mlbData.home.runsBattedIn) +
                    (mlbData.away.runsBattedIn)
                ),
              },
            },
            {
              label: "Home Runs",
              away: {
                title: `${mlbData.away.homeRuns}`,
                value: calculatePercentage(
                  mlbData.away.homeRuns,
                  (mlbData.away.homeRuns) +
                    (mlbData.home.homeRuns)
                ),
              },
              home: {
                title: `${mlbData.home.homeRuns}`,
                value: calculatePercentage(
                  mlbData.home.homeRuns,
                  (mlbData.home.homeRuns) +
                    (mlbData.away.homeRuns)
                ),
              },
            },
            {
              label: "Doubles",
              away: {
                title: `${mlbData.away.doubles}`,
                value: calculatePercentage(
                  mlbData.away.doubles,
                  mlbData.away.doubles + mlbData.home.doubles
                ),
              },
              home: {
                title: `${mlbData.home.doubles}`,
                value: calculatePercentage(
                  mlbData.home.doubles,
                  mlbData.home.doubles + mlbData.away.doubles
                ),
              },
            },
            {
              label: "Triples",
              away: {
                title: `${mlbData.away.triples}`,
                value: calculatePercentage(
                  mlbData.away.triples,
                  mlbData.away.triples + mlbData.home.triples
                ),
              },
              home: {
                title: `${mlbData.home.triples}`,
                value: calculatePercentage(
                  mlbData.home.triples,
                  mlbData.home.triples + mlbData.away.triples
                ),
              },
            },
            
            {
              label: "Bases on Balls / Walks",
              away: {
                title: `${mlbData.away.walks}`,
                value: calculatePercentage(
                  mlbData.away.walks,
                  (mlbData.away.walks) +
                    (mlbData.home.walks)
                ),
              },
              home: {
                title: `${mlbData.home.walks}`,
                value: calculatePercentage(
                  mlbData.home.walks,
                  (mlbData.home.walks) +
                    (mlbData.away.walks)
                ),
              },
            },
            {
              label: "On Base %",
              away: {
                title: `${removeLeadingZeroDecimal(mlbData.away.onBasePct)}`,
                value: calculatePercentage(
                  mlbData.away.onBasePct,
                  mlbData.away.onBasePct + mlbData.home.onBasePct
                ),
              },
              home: {
                title: `${removeLeadingZeroDecimal(mlbData.home.onBasePct)}`,
                value: calculatePercentage(
                  mlbData.home.onBasePct,
                  mlbData.home.onBasePct + mlbData.away.onBasePct
                ),
              },
            },
            
          ],
        },
      ];
    case "nhl":
      const nhlData = data as NHLGameTeamStats; 
      return [
        {
          group: "Offense",
          items: [
            {
              label: "Shots",
              away: {
                title: `${nhlData.away.shots}`,
                value: calculatePercentage(
                  nhlData.away.shots,
                  (nhlData.away.shots) +
                    (nhlData.home.shots)
                ),
              },
              home: {
                title: `${nhlData.home.shots}`,
                value: calculatePercentage(
                  nhlData.home.shots,
                  (nhlData.home.shots) +
                    (nhlData.away.shots)
                ),
              },
            },
            {
              label: "Faceoff %",
              away: {
                title: `${nhlData.away.faceoffPct.toFixed(1)}`,
                value: calculatePercentage(
                  nhlData.away.faceoffPct,
                  (nhlData.away.faceoffPct) +
                    (nhlData.home.faceoffPct)
                ),
              },
              home: {
                title: `${nhlData.home.faceoffPct.toFixed(1)}`,
                value: calculatePercentage(
                  nhlData.home.faceoffPct,
                  (nhlData.home.faceoffPct) +
                    (nhlData.away.faceoffPct)
                ),
              },
            },
            {
              label: "Power Play Goals",
              away: {
                title: `${nhlData.away.powerPlayGoals}`,
                value: calculatePercentage(
                  nhlData.away.powerPlayGoals,
                  nhlData.away.powerPlayGoals + nhlData.home.powerPlayGoals
                ),
              },
              home: {
                title: `${nhlData.home.powerPlayGoals}`,
                value: calculatePercentage(
                  nhlData.home.powerPlayGoals,
                  nhlData.home.powerPlayGoals + nhlData.away.powerPlayGoals
                ),
              },
            },
            {
              label: "Penalty Minutes",
              away: {
                title: `${nhlData.away.penaltyMinutes}`,
                value: calculatePercentage(
                  nhlData.away.penaltyMinutes,
                  (nhlData.away.penaltyMinutes) +
                    (nhlData.home.penaltyMinutes)
                ),
              },
              home: {
                title: `${nhlData.home.penaltyMinutes}`,
                value: calculatePercentage(
                  nhlData.home.penaltyMinutes,
                  (nhlData.home.penaltyMinutes) +
                    (nhlData.away.penaltyMinutes)
                ),
              },
            },
            {
              label: "Hits",
              away: {
                title: `${nhlData.away.hits}`,
                value: calculatePercentage(
                  nhlData.away.hits,
                  (nhlData.away.hits) +
                    (nhlData.home.hits)
                ),
              },
              home: {
                title: `${nhlData.home.hits}`,
                value: calculatePercentage(
                  nhlData.home.hits,
                  (nhlData.home.hits) +
                    (nhlData.away.hits)
                ),
              },
            },
          ],
        },
        {
          group: "Defense",
          items: [
            {
              label: "Blocked Shots",
              away: {
                title: `${nhlData.away.blockedShots}`,
                value: calculatePercentage(
                  nhlData.away.blockedShots,
                  (nhlData.away.blockedShots) +
                    (nhlData.home.blockedShots)
                ),
              },
              home: {
                title: `${nhlData.home.blockedShots}`,
                value: calculatePercentage(
                  nhlData.home.blockedShots,
                  (nhlData.home.blockedShots) +
                    (nhlData.away.blockedShots)
                ),
              },
            },
            {
              label: "Giveaways",
              away: {
                title: `${nhlData.away.giveaways}`,
                value: calculatePercentage(
                  nhlData.away.giveaways,
                  (nhlData.away.giveaways) +
                    (nhlData.home.giveaways)
                ),
              },
              home: {
                title: `${nhlData.home.giveaways}`,
                value: calculatePercentage(
                  nhlData.home.giveaways,
                  (nhlData.home.giveaways) +
                    (nhlData.away.giveaways)
                ),
              },
            },
            {
              label: "Takeaways",
              away: {
                title: `${nhlData.away.takeaways}`,
                value: calculatePercentage(
                  nhlData.away.takeaways,
                  (nhlData.away.takeaways) +
                    (nhlData.home.takeaways)
                ),
              },
              home: {
                title: `${nhlData.home.takeaways}`,
                value: calculatePercentage(
                  nhlData.home.takeaways,
                  (nhlData.home.takeaways) +
                    (nhlData.away.takeaways)
                ),
              },
            },
          ],
        },
      ];
    case "nba":
      const nbaData = data as NBAGameTeamStats; 
      return [
        {
          // group: "Offense",
          items: [
            {
              label: "Field Goals Made",
              away: {
                title: `${nbaData.away.fieldGoalsMade}`,
                value: calculatePercentage(
                  nbaData.away.fieldGoalsMade,
                  (nbaData.away.fieldGoalsMade) +
                    (nbaData.home.fieldGoalsMade)
                ),
              },
              home: {
                title: `${nbaData.home.fieldGoalsMade}`,
                value: calculatePercentage(
                  nbaData.home.fieldGoalsMade,
                  (nbaData.home.fieldGoalsMade) +
                    (nbaData.away.fieldGoalsMade)
                ),
              },
            },
            {
              label: "Field Goals Attempted",
              away: {
                title: `${nbaData.away.fieldGoalsAttempted}`,
                value: calculatePercentage(
                  nbaData.away.fieldGoalsAttempted,
                  (nbaData.away.fieldGoalsAttempted) +
                    (nbaData.home.fieldGoalsAttempted)
                ),
              },
              home: {
                title: `${nbaData.home.fieldGoalsAttempted}`,
                value: calculatePercentage(
                  nbaData.home.fieldGoalsAttempted,
                  (nbaData.home.fieldGoalsAttempted) +
                    (nbaData.away.fieldGoalsAttempted)
                ),
              },
            },
            {
              label: "Field Goal %",
              away: {
                title: `${nbaData.away.fieldGoalsPct}`,
                value: calculatePercentage(
                  nbaData.away.fieldGoalsPct,
                  (nbaData.away.fieldGoalsPct) +
                    (nbaData.home.fieldGoalsPct)
                ),
              },
              home: {
                title: `${nbaData.home.fieldGoalsPct}`,
                value: calculatePercentage(
                  nbaData.home.fieldGoalsPct,
                  (nbaData.home.fieldGoalsPct) +
                    (nbaData.away.fieldGoalsPct)
                ),
              },
            },
            {
              label: "3 Pointers Made",
              away: {
                title: `${nbaData.away.threePointersMade}`,
                value: calculatePercentage(
                  nbaData.away.threePointersMade,
                  (nbaData.away.threePointersMade) +
                    (nbaData.home.threePointersMade)
                ),
              },
              home: {
                title: `${nbaData.home.threePointersMade}`,
                value: calculatePercentage(
                  nbaData.home.threePointersMade,
                  (nbaData.home.threePointersMade) +
                    (nbaData.away.threePointersMade)
                ),
              },
            },
            {
              label: "3 Pointers Attempted",
              away: {
                title: `${nbaData.away.threePointersAttempted}`,
                value: calculatePercentage(
                  nbaData.away.threePointersAttempted,
                  (nbaData.away.threePointersAttempted) +
                    (nbaData.home.threePointersAttempted)
                ),
              },
              home: {
                title: `${nbaData.home.threePointersAttempted}`,
                value: calculatePercentage(
                  nbaData.home.threePointersAttempted,
                  (nbaData.home.threePointersAttempted) +
                    (nbaData.away.threePointersAttempted)
                ),
              },
            },
            {
              label: "3 Pointers %",
              away: {
                title: `${nbaData.away.threePointersPct}`,
                value: calculatePercentage(
                  nbaData.away.threePointersPct,
                  (nbaData.away.threePointersPct) +
                    (nbaData.home.threePointersPct)
                ),
              },
              home: {
                title: `${nbaData.home.threePointersPct}`,
                value: calculatePercentage(
                  nbaData.home.threePointersPct,
                  (nbaData.home.threePointersPct) +
                    (nbaData.away.threePointersPct)
                ),
              },
            },
            {
              label: "Free Throws Made",
              away: {
                title: `${nbaData.away.freeThrowsMade}`,
                value: calculatePercentage(
                  nbaData.away.freeThrowsMade,
                  (nbaData.away.freeThrowsMade) +
                    (nbaData.home.freeThrowsMade)
                ),
              },
              home: {
                title: `${nbaData.home.freeThrowsMade}`,
                value: calculatePercentage(
                  nbaData.home.freeThrowsMade,
                  (nbaData.home.freeThrowsMade) +
                    (nbaData.away.freeThrowsMade)
                ),
              },
            },
            {
              label: "Free Throws Attempted",
              away: {
                title: `${nbaData.away.freeThrowsAttempted}`,
                value: calculatePercentage(
                  nbaData.away.freeThrowsAttempted,
                  (nbaData.away.freeThrowsAttempted) +
                    (nbaData.home.freeThrowsAttempted)
                ),
              },
              home: {
                title: `${nbaData.home.freeThrowsAttempted}`,
                value: calculatePercentage(
                  nbaData.home.freeThrowsAttempted,
                  (nbaData.home.freeThrowsAttempted) +
                    (nbaData.away.freeThrowsAttempted)
                ),
              },
            },
            {
              label: "Free Throw %",
              away: {
                title: `${nbaData.away.freeThrowsPct}`,
                value: calculatePercentage(
                  nbaData.away.freeThrowsPct,
                  (nbaData.away.freeThrowsPct) +
                    (nbaData.home.freeThrowsPct)
                ),
              },
              home: {
                title: `${nbaData.home.freeThrowsPct}`,
                value: calculatePercentage(
                  nbaData.home.freeThrowsPct,
                  (nbaData.home.freeThrowsPct) +
                    (nbaData.away.freeThrowsPct)
                ),
              },
            },
            {
              label: "Rebounds",
              away: {
                title: `${nbaData.away.rebounds}`,
                value: calculatePercentage(
                  nbaData.away.rebounds,
                  (nbaData.away.rebounds) +
                    (nbaData.home.rebounds)
                ),
              },
              home: {
                title: `${nbaData.home.rebounds}`,
                value: calculatePercentage(
                  nbaData.home.rebounds,
                  (nbaData.home.rebounds) +
                    (nbaData.away.rebounds)
                ),
              },
            },
            {
              label: "Offensive Rebounds",
              away: {
                title: `${nbaData.away.offensiveRebounds}`,
                value: calculatePercentage(
                  nbaData.away.offensiveRebounds,
                  (nbaData.away.offensiveRebounds) +
                    (nbaData.home.offensiveRebounds)
                ),
              },
              home: {
                title: `${nbaData.home.offensiveRebounds}`,
                value: calculatePercentage(
                  nbaData.home.offensiveRebounds,
                  (nbaData.home.offensiveRebounds) +
                    (nbaData.away.offensiveRebounds)
                ),
              },
            },
            
            {
              label: "Defensive Rebounds",
              away: {
                title: `${nbaData.away.defensiveRebounds}`,
                value: calculatePercentage(
                  nbaData.away.defensiveRebounds,
                  (nbaData.away.defensiveRebounds) +
                    (nbaData.home.defensiveRebounds)
                ),
              },
              home: {
                title: `${nbaData.home.defensiveRebounds}`,
                value: calculatePercentage(
                  nbaData.home.defensiveRebounds,
                  (nbaData.home.defensiveRebounds) +
                    (nbaData.away.defensiveRebounds)
                ),
              },
            },
            {
              label: "Assists",
              away: {
                title: `${nbaData.away.assists}`,
                value: calculatePercentage(
                  nbaData.away.assists,
                  (nbaData.away.assists) +
                    (nbaData.home.assists)
                ),
              },
              home: {
                title: `${nbaData.home.assists}`,
                value: calculatePercentage(
                  nbaData.home.assists,
                  (nbaData.home.assists) +
                    (nbaData.away.assists)
                ),
              },
            },
            {
              label: "Steals",
              away: {
                title: `${nbaData.away.steals}`,
                value: calculatePercentage(
                  nbaData.away.steals,
                  (nbaData.away.steals) +
                    (nbaData.home.steals)
                ),
              },
              home: {
                title: `${nbaData.home.steals}`,
                value: calculatePercentage(
                  nbaData.home.steals,
                  (nbaData.home.steals) +
                    (nbaData.away.steals)
                ),
              },
            },
            {
              label: "Blocks",
              away: {
                title: `${nbaData.away.blocks}`,
                value: calculatePercentage(
                  nbaData.away.blocks,
                  (nbaData.away.blocks) +
                    (nbaData.home.blocks)
                ),
              },
              home: {
                title: `${nbaData.home.blocks}`,
                value: calculatePercentage(
                  nbaData.home.blocks,
                  (nbaData.home.blocks) +
                    (nbaData.away.blocks)
                ),
              },
            },
            {
              label: "Turnovers",
              away: {
                title: `${nbaData.away.turnovers}`,
                value: calculatePercentage(
                  nbaData.away.turnovers,
                  (nbaData.away.turnovers) +
                    (nbaData.home.turnovers)
                ),
              },
              home: {
                title: `${nbaData.home.turnovers}`,
                value: calculatePercentage(
                  nbaData.home.turnovers,
                  (nbaData.home.turnovers) +
                    (nbaData.away.turnovers)
                ),
              },
            },
            {
              label: "Fouls",
              away: {
                title: `${nbaData.away.fouls}`,
                value: calculatePercentage(
                  nbaData.away.fouls,
                  (nbaData.away.fouls) +
                    (nbaData.home.fouls)
                ),
              },
              home: {
                title: `${nbaData.home.fouls}`,
                value: calculatePercentage(
                  nbaData.home.fouls,
                  (nbaData.home.fouls) +
                    (nbaData.away.fouls)
                ),
              },
            },
          ],
        },
        
      ];

    default:
      return [];
  }
}